
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Loader2 } from 'lucide-react';

export function ProtectedRoute() {
    const { user, loading: authLoading } = useAuth();
    const { user: userProfile, isLoading: profileLoading } = useUserProfile();
    const location = useLocation();

    // Show loader while auth or profile is loading
    if (authLoading || (user && profileLoading && !userProfile)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-background">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!user) {
        // Redirect to login page but save the attempted location
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check if user is a CLIENT trying to access trainer-only routes
    const isClient = userProfile?.role === 'CLIENT';
    const isClientWelcomePage = location.pathname === '/client-welcome';
    const isDashboardRoute = location.pathname.startsWith('/dashboard');

    // If role is still loading (undefined), wait for it before making decisions
    if (!userProfile?.role && isDashboardRoute) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-background">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // If client tries to access dashboard routes, redirect to client welcome
    if (isClient && isDashboardRoute && !isClientWelcomePage) {
        return <Navigate to="/client-welcome" replace />;
    }

    // If trainer ends up on client-welcome (e.g. after switching accounts), redirect to dashboard
    if (!isClient && userProfile?.role && isClientWelcomePage) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
}
